import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import KingBedOutlinedIcon from "@mui/icons-material/KingBedOutlined";
import dayjs from "dayjs";
import { palette, typography } from "theme/tokens";
import { kindMeta, kindOf } from "./requestKinds";

/**
 * Small pieces shared by the hotel manual-request UI: the Modify modal's
 * "Request sent" view, the manual-request rows of both portals' Servicing
 * Requests tables (cells + ModifyRequestExpanded) and ModifyQuoteDialog.
 * Colours are tokens; the brand colour (theme primary) marks what needs the
 * operator — a quote waiting for a decision, the current flow step.
 * Manual CANCELLATION requests use the same pieces; their wording comes from
 * requestKinds (KIND_META) by the request's `kind`.
 */

export const fmtYmd = (s) => {
  const d = dayjs(String(s || "").slice(0, 10), "YYYY-MM-DD", true);
  return d.isValid() ? d.format("DD MMM YYYY") : "—";
};

export const fmtDateTime = (s) => {
  const d = dayjs(s);
  return s && d.isValid() ? d.format("DD MMM YYYY, HH:mm") : "—";
};

export const fmtMoney = (n, cur) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${cur || ""} ${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();
};

const STATUS_META = {
  requested: { label: "Requested", bg: palette.tw.blue[50], color: palette.tw.blue[700] },
  in_review: { label: "In review", bg: palette.tw.amber[50], color: palette.tw.amber[800] },
  quote_ready: { label: "Quote ready", brand: true },
  quote_approved: { label: "Quote accepted", bg: palette.tw.emerald[50], color: palette.tw.emerald[800] },
  quote_rejected: { label: "Quote rejected", bg: palette.tw.red[50], color: palette.tw.red[800] },
  completed: { label: "Modified", bg: palette.tw.emerald[100], color: palette.tw.emerald[800] },
  cancelled: { label: "Cancelled", bg: palette.tw.gray[100], color: palette.tw.gray[600] },
  withdrawn: { label: "Withdrawn", bg: palette.tw.gray[100], color: palette.tw.gray[600] },
  failed: { label: "Failed", bg: palette.tw.red[100], color: palette.tw.red[800] },
  // The live hotel manual-request API's own statuses (hotelManualRequestAPI).
  open: { label: "Open", bg: palette.tw.blue[50], color: palette.tw.blue[700] },
  quoted: { label: "Quote ready", brand: true },
  rejected: { label: "Quote rejected", bg: palette.tw.red[50], color: palette.tw.red[800] },
  withdrawn: { label: "Withdrawn", bg: palette.tw.gray[100], color: palette.tw.gray[600] },
};

export function StatusPill({ status, kind }) {
  const theme = useTheme();
  const base = STATUS_META[status] || {
    label: String(status || "—").replace(/_/g, " "),
    bg: palette.tw.gray[100],
    color: palette.tw.gray[600],
  };
  // "Modified" / "Hotel cancelled" — what completing means for this kind.
  const meta = status === "completed" ? { ...base, label: kindMeta(kind).completedLabel } : base;
  const brand = theme.palette.primary.main;
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        height: 22,
        fontSize: typography.fontSize[11],
        fontWeight: typography.fontWeight.semiBold,
        bgcolor: meta.brand ? alpha(brand, 0.12) : meta.bg,
        color: meta.brand ? brand : meta.color,
      }}
    />
  );
}

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; }
  70% { box-shadow: 0 0 0 6px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

/** The "new message" pill on a request whose quote waits for a decision.
 *  With `label` it is an action pill instead (CRM "Create quote" / "Re-quote"):
 *  filled when `isNew`, no pulsing dot. */
export function NewQuotePill({ isNew, onClick, label }) {
  const theme = useTheme();
  const brand = theme.palette.primary.main;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 24,
        px: 1.25,
        border: "none",
        borderRadius: "999px",
        cursor: "pointer",
        bgcolor: isNew ? brand : alpha(brand, 0.1),
        color: isNew ? theme.palette.primary.contrastText : brand,
        fontFamily: "inherit",
        fontSize: typography.fontSize[11],
        fontWeight: typography.fontWeight.bold,
        transition: "transform 120ms ease, background-color 120ms ease",
        "&:hover": { transform: "translateY(-1px)" },
        "&:focus-visible": { outline: `2px solid ${alpha(brand, 0.4)}`, outlineOffset: 2 },
      }}
    >
      {isNew && !label && (
        <Box
          component="span"
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: "currentColor",
            color: theme.palette.primary.contrastText,
            animation: `${pulse} 1.6s ease-out infinite`,
          }}
        />
      )}
      {label || (isNew ? "New quote" : "View quote")}
    </Box>
  );
}

const changeRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexWrap: "wrap",
  fontSize: typography.fontSize[12],
  color: palette.neutral[670],
};

/** What a request asks to change, per hotel: dates old → new, room old → new. */
export function RequestChanges({ hotels }) {
  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      {(hotels || []).map((h, i) => (
        <Box key={`${h.hotelRowId || "hotel"}-${i}`}>
          <Typography
            sx={{
              fontSize: typography.fontSize[13],
              fontWeight: typography.fontWeight.semiBold,
              color: palette.neutral[870],
              mb: 0.5,
            }}
          >
            {h.hotelName || "Hotel"}
          </Typography>
          {h.dates && (
            <Box sx={changeRowSx}>
              <EventOutlinedIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470] }} />
              <span>
                {fmtYmd(h.dates.from?.checkIn)} – {fmtYmd(h.dates.from?.checkOut)}
              </span>
              <ArrowForwardIcon sx={{ fontSize: typography.fontSize[14], color: "primary.main" }} />
              <Box component="span" sx={{ fontWeight: typography.fontWeight.semiBold, color: palette.neutral[870] }}>
                {fmtYmd(h.dates.to?.checkIn)} – {fmtYmd(h.dates.to?.checkOut)}
              </Box>
            </Box>
          )}
          {h.room && (
            <Box sx={{ ...changeRowSx, mt: h.dates ? 0.5 : 0 }}>
              <KingBedOutlinedIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470] }} />
              <span>
                {[h.room.from?.roomName, h.room.from?.boardType].filter(Boolean).join(" · ") || "Current room"}
              </span>
              <ArrowForwardIcon sx={{ fontSize: typography.fontSize[14], color: "primary.main" }} />
              <Box component="span" sx={{ fontWeight: typography.fontWeight.semiBold, color: palette.neutral[870] }}>
                {h.room.to?.description || "—"}
              </Box>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

/* ── Table helpers ───────────────────────────────────────────────────── */

/** "Golden Tulip Lucknow" / "Hotel A + 1 more" — the hotels a request is about. */
export const manualHotelsLabel = (request) => {
  const names = (request?.hotels || []).map((h) => h.hotelName || "Hotel");
  if (!names.length) return "Hotel";
  return names.length === 1 ? names[0] : `${names[0]} + ${names.length - 1} more`;
};

/** "Dates · Room upgrade" — what a request asks to change ("Cancellation"
 *  for a cancellation request). */
export const manualChangeSummary = (request) => {
  if (kindOf(request) === "cancel") return "Cancellation";
  const hotels = request?.hotels || [];
  const parts = [];
  if (hotels.some((h) => h.dates)) parts.push("Dates");
  if (hotels.some((h) => h.room)) parts.push("Room upgrade");
  return parts.join(" · ") || "Modification";
};

/** The stays a cancellation request asks to cancel: name, dates, room. */
export function CancelStays({ hotels }) {
  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      {(hotels || []).map((h, i) => (
        <Box key={`${h.hotelRowId || "hotel"}-${i}`}>
          <Typography
            sx={{
              fontSize: typography.fontSize[13],
              fontWeight: typography.fontWeight.semiBold,
              color: palette.neutral[870],
              mb: 0.25,
            }}
          >
            {h.hotelName || "Hotel"}
          </Typography>
          <Box sx={changeRowSx}>
            <EventOutlinedIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470] }} />
            <span>
              {fmtYmd(h.checkIn)} – {fmtYmd(h.checkOut)}
            </span>
            {(h.roomName || h.boardType) && (
              <>
                <KingBedOutlinedIcon sx={{ fontSize: typography.fontSize[14], color: palette.neutral[470], ml: 1 }} />
                <span>{[h.roomName, h.boardType].filter(Boolean).join(" · ")}</span>
              </>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** What a request is about, by kind: dates / room changes, or stays to cancel. */
export function RequestSubject({ request, hotels }) {
  const list = hotels || request?.hotels;
  return kindOf(request) === "cancel" ? <CancelStays hotels={list} /> : <RequestChanges hotels={list} />;
}

/** Net of the request's quote: > 0 due, < 0 refund, null when there is none. */
export const quoteNet = (request) =>
  request?.quote ? request.quote.amountDue - request.quote.refundAmount : null;

/**
 * The Financial Impact cell of a manual request: the quote's amount with its
 * pill ("New quote" until opened, then "View quote") — a click opens the quote
 * without toggling the row — or what is known so far.
 */
export function QuoteImpact({ request, currency, isNew, onOpenQuote, caption }) {
  const net = quoteNet(request);
  if (net === null) {
    const done = request?.status === "completed";
    return (
      <Typography sx={{ fontSize: typography.fontSize[13], color: palette.neutral[470] }}>
        {done ? "No price change" : ["cancelled", "withdrawn", "failed"].includes(request?.status) ? "—" : "Awaiting review"}
      </Typography>
    );
  }
  const cur = request.quote.currency || currency;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: typography.fontSize[13],
          fontWeight: typography.fontWeight.semiBold,
          color: net > 0 ? palette.tw.red[800] : net < 0 ? palette.tw.emerald[800] : palette.neutral[600],
        }}
      >
        {net > 0 ? `+ ${fmtMoney(net, cur)}` : net < 0 ? `− ${fmtMoney(-net, cur)} refund` : "No change"}
      </Typography>
      {caption && (
        <Typography sx={{ fontSize: typography.fontSize[11], color: palette.neutral[470] }}>{caption}</Typography>
      )}
      <Box component="span" onClick={(e) => e.stopPropagation()}>
        <NewQuotePill
          isNew={request.status === "quote_ready" && isNew}
          onClick={() => onOpenQuote?.(request)}
        />
      </Box>
    </Box>
  );
}

/* ── Request chains (re-quote rounds) ────────────────────────────────────
   Approving a quote makes the backend open a follow-up request
   (`parentRequestId`) that RE-QUOTES the change. A request and all its
   follow-ups are ONE chain, shown as one row: the root's id and change, the
   latest round's status, quote and position in the flow. */

/** Group requests into chains: [{ id, root, rounds (oldest first), latest }],
 *  newest root first. Follow-ups whose parent is missing start their own. */
export const groupModifyRequestChains = (requests) => {
  const list = requests || [];
  const byId = new Map(list.map((r) => [r.requestId, r]));
  const rootOf = (r) => {
    let cur = r;
    const seen = new Set();
    while (cur.parentRequestId && byId.has(cur.parentRequestId) && !seen.has(cur.requestId)) {
      seen.add(cur.requestId);
      cur = byId.get(cur.parentRequestId);
    }
    return cur;
  };
  const groups = new Map();
  list.forEach((r) => {
    const root = rootOf(r);
    if (!groups.has(root.requestId)) groups.set(root.requestId, { root, rounds: [] });
    groups.get(root.requestId).rounds.push(r);
  });
  const byTime = (a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
  return Array.from(groups.values())
    .map(({ root, rounds }) => {
      const sorted = rounds.slice().sort(byTime);
      return { id: root.requestId, root, rounds: sorted, latest: sorted[sorted.length - 1] };
    })
    .sort((a, b) => byTime(b.root, a.root));
};

/** A chain from a chain or a single request. */
export const toChain = (x) =>
  x?.rounds ? x : { id: x?.requestId, root: x, rounds: x ? [x] : [], latest: x };

const isQuoted = (r) =>
  !!r?.quote || ["quote_ready", "quote_approved", "quote_rejected"].includes(r?.status);

/** Not answered yet — a quote may still come. */
const isPending = (r) => r?.status === "requested" || r?.status === "in_review";

const SETTLED = ["quote_ready", "quote_approved", "quote_rejected", "completed", "cancelled", "withdrawn", "failed"];

/**
 * Flow progress of a chain — one plain, separately named step per thing that
 * happens, in order (no "(2)" round suffixes); the list grows with every
 * round and drops what a round turns out not to need:
 *
 *   In Progress → Checking Supplier
 *   → Quote Ready → Customer Review → Accepted       (round 1 priced)
 *   → Re-quote                                        (each follow-up round)
 *     [→ New Quote → Customer Review → Accepted → Re-quote …]   (priced again)
 *   → Confirmed → Complete
 *
 *   price unchanged on re-quote:  … ✓ Accepted ✓ Re-quote ✓ Confirmed ✓ Complete
 *   new price on re-quote:        … ✓ Re-quote ✓ New Quote ● Customer Review → Accepted → Re-quote → …
 *   rejected:                     … ✓ Customer Review ✕ Rejected   (ends)
 *   cancelled / failed:           red Cancelled / Failed where it stopped
 *
 * Steps not reached yet are what can still happen: a new request shows the
 * whole priced path (no price change → it shrinks to In Progress, Checking
 * Supplier, Confirmed, Complete); a re-quote in progress shows → Confirmed →
 * Complete, and a new quote adds its own steps.
 *
 * Each step carries its state: done | current | error | todo.
 */
const chainFlow = (chainOrRequest) => {
  const chain = toChain(chainOrRequest);
  const { rounds } = chain;
  // Cancellation: Refund Quote / New Refund Quote / Cancelled.
  const words = kindMeta(chain).flow;
  const steps = [];
  const add = (label, state) => steps.push({ key: `${steps.length}-${label}`, label, state });
  const todo = (...labels) => labels.forEach((l) => add(l, "todo"));
  rounds.forEach((r, i) => {
    const last = i === rounds.length - 1;
    const s = r?.status;
    const quoteLabel = i === 0 ? words.quote : words.newQuote;
    // Unknown statuses read as still in progress.
    const pending = last && !SETTLED.includes(s);

    // One request that keeps its rounds (the activity manual-request API):
    // round 2+ and open = the quote was accepted, Zenvoya completes next.
    if (rounds.length === 1 && r?.round > 1 && s === "requested") {
      ["In Progress", "Checking Supplier", quoteLabel, "Customer Review", "Accepted"].forEach((l) => add(l, "done"));
      add(words.done, "current");
      todo("Complete");
      return;
    }

    if (i === 0) {
      add("In Progress", pending && s === "requested" ? "current" : "done");
      add("Checking Supplier", pending ? (s === "requested" ? "todo" : "current") : "done");
    } else {
      add("Re-quote", pending ? "current" : "done");
    }

    if (!last) {
      // An earlier round: its quote was approved — that is what opened the next.
      if (isQuoted(r)) {
        add(quoteLabel, "done");
        add("Customer Review", "done");
        add("Accepted", "done");
      }
      return;
    }
    if (pending) {
      if (i === 0) todo(words.quote, "Customer Review", "Accepted", "Re-quote");
      todo(words.done, "Complete");
      return;
    }
    switch (s) {
      case "quote_ready":
        add(quoteLabel, "done");
        add("Customer Review", "current");
        todo("Accepted", "Re-quote", words.done, "Complete");
        return;
      case "quote_approved":
        // Accepted; its re-quote round is about to appear.
        add(quoteLabel, "done");
        add("Customer Review", "done");
        add("Accepted", "done");
        add("Re-quote", "current");
        todo(words.done, "Complete");
        return;
      case "quote_rejected":
        add(quoteLabel, "done");
        add("Customer Review", "done");
        add("Rejected", "error");
        return;
      case "completed":
        if (r.quote) {
          add(quoteLabel, "done");
          add("Customer Review", "done");
          add("Accepted", "done");
        }
        add(words.done, "done");
        add("Complete", "done");
        return;
      default:
        // cancelled / failed
        if (r.quote) {
          add(quoteLabel, "done");
          add("Customer Review", "done");
        }
        add(s === "failed" ? "Failed" : s === "withdrawn" ? "Withdrawn" : "Cancelled", "error");
    }
  });
  return steps;
};

/**
 * The Financial Impact cell of a chain: the latest round's quote; while a
 * re-quote is in progress (or once it confirmed the price) the last APPROVED
 * quote stays on show with what is happening to it — a re-quote is not "no
 * price change".
 */
export function ChainImpact({ chain, currency, isQuoteNew, onOpenQuote, onCreateQuote }) {
  const { rounds, latest } = toChain(chain);
  const meta = kindMeta(toChain(chain));
  const cancel = meta.kind === "cancel";
  const lastQuoted = rounds.slice().reverse().find((r) => r.quote) || null;
  // CRM: a round waiting for review gets its action right here.
  const act = !!onCreateQuote && isPending(latest);
  const actPill = act && (
    <Box component="span" onClick={(e) => e.stopPropagation()}>
      <NewQuotePill
        isNew
        label={latest.parentRequestId ? "Re-quote" : meta.createQuote}
        onClick={() => onCreateQuote(latest)}
      />
    </Box>
  );
  if (latest?.quote || !lastQuoted) {
    if (act) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
          <Typography sx={{ fontSize: typography.fontSize[13], color: palette.neutral[470] }}>
            Awaiting your review
          </Typography>
          {actPill}
        </Box>
      );
    }
    return (
      <QuoteImpact
        request={latest}
        currency={currency}
        isNew={isQuoteNew?.(latest)}
        onOpenQuote={onOpenQuote}
      />
    );
  }
  const caption = isPending(latest)
    ? act
      ? cancel
        ? "Accepted · confirm or re-quote"
        : "Accepted · check the price again"
      : cancel
      ? "Accepted · Zenvoya is confirming the cancellation"
      : "Accepted · Zenvoya is re-checking the price"
    : latest?.status === "completed"
    ? cancel
      ? "Accepted · cancellation confirmed"
      : "Accepted · price confirmed"
    : null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
      <QuoteImpact
        request={lastQuoted}
        currency={currency}
        isNew={false}
        onOpenQuote={onOpenQuote}
        caption={caption}
      />
      {actPill}
    </Box>
  );
}

/** "Round 2" once a chain has been re-quoted, else "". */
export const chainRoundLabel = (chain) => {
  const n = toChain(chain).rounds.length;
  return n > 1 ? `Round ${n}` : "";
};

/** `chain` (groupModifyRequestChains) or a single `request` — or ready-made
 *  `steps` ([{ key, label, state: done | current | error | todo }]). */
export function ModifyRequestStepper({ chain, request, steps: given }) {
  const theme = useTheme();
  const brand = theme.palette.primary.main;
  const steps = given || chainFlow(chain || request);
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", overflow: "auto", py: 1 }}>
      {steps.map((step, i) => {
        const done = step.state === "done";
        const isError = step.state === "error";
        const current = step.state === "current" || isError;
        const color = done
          ? palette.tw.emerald[600]
          : isError
          ? palette.tw.red[600]
          : current
          ? brand
          : palette.tw.gray[300];
        const bg = done
          ? palette.tw.emerald[100]
          : isError
          ? palette.tw.red[100]
          : current
          ? alpha(brand, 0.12)
          : palette.tw.gray[100];
        return (
          <React.Fragment key={step.key}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 72 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: bg,
                  border: `2px solid ${color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: typography.fontSize[12],
                  fontWeight: typography.fontWeight.bold,
                  color,
                }}
              >
                {done ? "✓" : isError ? "✕" : i + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: typography.fontSize[10],
                  fontWeight: typography.fontWeight.semiBold,
                  color: done ? palette.tw.emerald[600] : current ? color : palette.tw.gray[400],
                  mt: 0.5,
                  textAlign: "center",
                  lineHeight: typography.lineHeight.tight,
                  maxWidth: 72,
                }}
              >
                {step.label}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  bgcolor: done ? palette.tw.emerald[600] : palette.tw.gray[200],
                  mt: "11px",
                  minWidth: 12,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
